import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Loader2, Plus, X, Check, ToggleLeft, ToggleRight,
  MessageSquare, ExternalLink, CheckCircle2, XCircle, Clock,
  Settings2,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CommentConfig {
  id: string
  user_id: number
  agent_connection_id: string
  texts: string[]
  auto_enabled: boolean
  max_posts_per_run: number
  interval_hours: number
  last_run_at: string | null
  connection: {
    id: string
    fb_page_name: string
    fb_page_id: string | null
    agent_profile_id: string
  } | null
}

interface CommentedPost {
  id: string
  agent_connection_id: string
  page_name: string | null
  post_url: string | null
  comment_text: string | null
  status: string
  error_message: string | null
  commented_at: number | null
}

interface Connection {
  id: string
  fb_page_name: string
  agent_profile_name: string
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function CommentsTab() {
  const qc = useQueryClient()

  // ── Queries ──
  const { data: configsRaw, isLoading: loadingConfigs } = useQuery<{ configs: CommentConfig[] }>({
    queryKey: ['automacao-fb', 'all-comment-configs'],
    queryFn: () => api.get('/automacao-fb/comment-configs'),
    refetchInterval: 30000,
  })

  const { data: postsRaw, isLoading: loadingPosts } = useQuery<{ posts: CommentedPost[] }>({
    queryKey: ['automacao-fb', 'all-commented-posts'],
    queryFn: () => api.get('/automacao-fb/commented-posts'),
    refetchInterval: 30000,
  })

  const { data: connsRaw } = useQuery<{ connections: Connection[] }>({
    queryKey: ['automacao-fb', 'connections'],
    queryFn: () => api.get('/automacao-fb/connections'),
  })

  const configs = configsRaw?.configs ?? []
  const posts = postsRaw?.posts ?? []
  const connections = connsRaw?.connections ?? []

  // Connections without config (for "Add config" dropdown)
  const unconfigured = useMemo(() => {
    const configuredIds = new Set(configs.map(c => c.agent_connection_id))
    return connections.filter(c => !configuredIds.has(c.id))
  }, [configs, connections])

  // Stats
  const stats = useMemo(() => ({
    total: posts.length,
    success: posts.filter(p => p.status === 'success').length,
    failed: posts.filter(p => p.status !== 'success').length,
    activeConfigs: configs.filter(c => c.auto_enabled).length,
  }), [posts, configs])

  const isLoading = loadingConfigs || loadingPosts

  return (
    <div className="space-y-6">
      {/* ── Stats bar ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#111113] border border-[#27272a]">
          <Settings2 className="h-3.5 w-3.5 text-zinc-500" />
          <span className="text-xs text-zinc-400">{configs.length} configs</span>
          <span className="text-[10px] text-emerald-400 font-medium">({stats.activeConfigs} ativos)</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-xs text-emerald-300">{stats.success} comentarios</span>
        </div>
        {stats.failed > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/10">
            <XCircle className="h-3.5 w-3.5 text-red-400" />
            <span className="text-xs text-red-300">{stats.failed} falharam</span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : (
        <>
          {/* ── Configs section ── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-indigo-400" />
                <h3 className="text-sm font-semibold text-zinc-300">Configuracoes por pagina</h3>
              </div>
            </div>

            {configs.length === 0 && unconfigured.length === 0 ? (
              <div className="rounded-lg border border-[#27272a] bg-[#0c0c10] p-6 text-center">
                <MessageSquare className="h-6 w-6 text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-600">Nenhuma pagina conectada para configurar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {configs.map(cfg => (
                  <ConfigCard key={cfg.id} config={cfg} />
                ))}

                {/* Add config for unconfigured connections */}
                {unconfigured.length > 0 && (
                  <AddConfigCard connections={unconfigured} />
                )}
              </div>
            )}
          </section>

          {/* ── History section ── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-4 w-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-zinc-300">Historico de comentarios</h3>
            </div>

            {posts.length === 0 ? (
              <div className="rounded-lg border border-[#27272a] bg-[#0c0c10] p-6 text-center">
                <MessageSquare className="h-6 w-6 text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-600">Nenhum comentario registrado ainda</p>
              </div>
            ) : (
              <div className="rounded-lg border border-[#27272a] overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_100px_120px_40px] gap-3 px-4 py-2.5 bg-[#0c0c10] border-b border-[#27272a]">
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Pagina</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Comentario</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Status</span>
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Data</span>
                  <span />
                </div>

                <div className="divide-y divide-[#1e1e22] max-h-[400px] overflow-y-auto">
                  {posts.map(post => (
                    <div
                      key={post.id}
                      className="grid grid-cols-[1fr_1fr_100px_120px_40px] gap-3 px-4 py-3 items-center hover:bg-[#111113] transition-colors"
                    >
                      <span className="text-xs text-zinc-400 truncate">{post.page_name || '—'}</span>

                      <div className="min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{post.comment_text || '—'}</p>
                        {post.error_message && (
                          <p className="text-[10px] text-red-400/70 truncate mt-0.5">{post.error_message}</p>
                        )}
                      </div>

                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                        post.status === 'success' ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {post.status === 'success'
                          ? <CheckCircle2 className="h-3 w-3" />
                          : <XCircle className="h-3 w-3" />
                        }
                        {post.status === 'success' ? 'OK' : 'Erro'}
                      </span>

                      <span className="text-[11px] text-zinc-600">
                        {post.commented_at
                          ? new Date(post.commented_at).toLocaleString('pt-BR', {
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                            })
                          : '—'
                        }
                      </span>

                      {post.post_url ? (
                        <a
                          href={post.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-600 hover:text-indigo-400 transition-colors"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Config Card                                                        */
/* ------------------------------------------------------------------ */

function ConfigCard({ config }: { config: CommentConfig }) {
  const qc = useQueryClient()
  const [texts, setTexts] = useState<string[]>(config.texts || [])
  const [autoEnabled, setAutoEnabled] = useState(config.auto_enabled)
  const [maxPosts, setMaxPosts] = useState(config.max_posts_per_run)
  const [interval, setInterval_] = useState(config.interval_hours)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setTexts(config.texts || [])
    setAutoEnabled(config.auto_enabled)
    setMaxPosts(config.max_posts_per_run)
    setInterval_(config.interval_hours)
    setDirty(false)
  }, [config])

  const markDirty = () => setDirty(true)

  const save = useMutation({
    mutationFn: () =>
      api.post('/automacao-fb/comment-configs', {
        connection_id: config.agent_connection_id,
        texts: texts.filter(t => t.trim()),
        auto_enabled: autoEnabled,
        max_posts_per_run: maxPosts,
        interval_hours: interval,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-comment-configs'] })
      toast.success('Config salva')
      setDirty(false)
    },
    onError: () => toast.error('Erro ao salvar'),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/automacao-fb/comment-configs/${config.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-comment-configs'] })
      toast.success('Config removida')
    },
  })

  return (
    <div className={`rounded-lg border p-4 transition-colors ${
      autoEnabled
        ? 'border-emerald-500/15 bg-emerald-500/[0.02]'
        : 'border-[#27272a] bg-[#0c0c10]'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setAutoEnabled(!autoEnabled); markDirty() }}
          >
            {autoEnabled
              ? <ToggleRight className="h-5 w-5 text-emerald-400" />
              : <ToggleLeft className="h-5 w-5 text-zinc-600" />
            }
          </button>
          <div>
            <p className="text-sm text-zinc-200 font-medium">{config.connection?.fb_page_name ?? '—'}</p>
            <p className="text-[10px] text-zinc-600">
              {autoEnabled ? 'Ativo' : 'Desativado'}
              {config.last_run_at && ` · Ultimo: ${new Date(config.last_run_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => { if (confirm('Remover config?')) remove.mutate() }}
          className="text-zinc-600 hover:text-red-400 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Settings row */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-500">Ultimos</span>
          <input
            type="number"
            min={1}
            max={50}
            value={maxPosts}
            onChange={e => { setMaxPosts(Math.max(1, Math.min(50, parseInt(e.target.value) || 5))); markDirty() }}
            className="w-14 rounded-md border border-[#27272a] bg-[#111113] px-2 py-1 text-xs text-zinc-300 text-center focus:border-indigo-500/50 focus:outline-none"
          />
          <span className="text-[11px] text-zinc-500">posts</span>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-zinc-600" />
          <span className="text-[11px] text-zinc-500">A cada</span>
          <select
            value={interval}
            onChange={e => { setInterval_(parseInt(e.target.value)); markDirty() }}
            className="rounded-md border border-[#27272a] bg-[#111113] px-2 py-1 text-xs text-zinc-300 focus:outline-none"
          >
            <option value={12}>12h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={72}>72h</option>
          </select>
        </div>
      </div>

      {/* Texts */}
      <div className="space-y-2 mb-3">
        {texts.map((text, idx) => (
          <div key={idx} className="flex gap-2">
            <textarea
              value={text}
              onChange={e => {
                const updated = [...texts]
                updated[idx] = e.target.value
                setTexts(updated)
                markDirty()
              }}
              rows={2}
              placeholder={`Comentario ${idx + 1}`}
              className="flex-1 rounded-md border border-[#27272a] bg-[#111113] px-3 py-2 text-xs text-zinc-300 placeholder:text-zinc-700 focus:border-indigo-500/50 focus:outline-none resize-none"
            />
            <button
              onClick={() => { setTexts(texts.filter((_, i) => i !== idx)); markDirty() }}
              className="text-zinc-600 hover:text-red-400 transition-colors self-start mt-2 shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          onClick={() => { setTexts([...texts, '']); markDirty() }}
          className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          <Plus className="h-3 w-3" />
          Adicionar texto
        </button>
      </div>

      {/* Save */}
      {dirty && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-xs text-white font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Salvar alteracoes
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Add Config Card                                                    */
/* ------------------------------------------------------------------ */

function AddConfigCard({ connections }: { connections: Connection[] }) {
  const qc = useQueryClient()
  const [selectedConn, setSelectedConn] = useState('')

  const create = useMutation({
    mutationFn: (connectionId: string) =>
      api.post('/automacao-fb/comment-configs', {
        connection_id: connectionId,
        texts: [''],
        auto_enabled: false,
        max_posts_per_run: 5,
        interval_hours: 24,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-comment-configs'] })
      toast.success('Config criada')
      setSelectedConn('')
    },
    onError: () => toast.error('Erro ao criar config'),
  })

  return (
    <div className="rounded-lg border border-dashed border-[#27272a] bg-[#0c0c10] p-4">
      <div className="flex items-center gap-3">
        <Plus className="h-4 w-4 text-zinc-600 shrink-0" />
        <select
          value={selectedConn}
          onChange={e => setSelectedConn(e.target.value)}
          className="flex-1 rounded-md border border-[#27272a] bg-[#111113] px-3 py-2 text-xs text-zinc-400 focus:outline-none"
        >
          <option value="">Adicionar config para...</option>
          {connections.map(c => (
            <option key={c.id} value={c.id}>{c.fb_page_name} ({c.agent_profile_name})</option>
          ))}
        </select>
        <button
          onClick={() => { if (selectedConn) create.mutate(selectedConn) }}
          disabled={!selectedConn || create.isPending}
          className="px-4 py-2 rounded-md bg-indigo-600 text-xs text-white font-medium hover:bg-indigo-700 disabled:opacity-30 transition-colors"
        >
          Criar
        </button>
      </div>
    </div>
  )
}
